import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getMakes, getProduct, createProduct, updateProduct, getModelsByMake } from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardHeader, CardContent, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Separator } from '../../components/ui/separator';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { ChevronLeft, Save, AlertCircle, RefreshCw } from 'lucide-react';

const PART_CATEGORIES = ['Engine & Drivetrain', 'Bodywork', 'Electrical', 'Suspension & Wheels', 'Apparel', 'Accessories'];
const PART_TYPES = {
  'Engine & Drivetrain': ['Engine', 'Transmission', 'Exhaust', 'Clutch', 'Chain & Sprockets', 'Belt & Pulleys', 'Cooling System'],
  'Bodywork': ['Fairing', 'Tank', 'Seat', 'Fender', 'Panel', 'Windscreen'],
  'Electrical': ['Headlight', 'Taillight', 'Battery', 'Regulator', 'Stator', 'Switch', 'Wiring'],
  'Suspension & Wheels': ['Fork', 'Shock Absorber', 'Wheel', 'Brake Caliper', 'Brake Disc', 'Swingarm'],
  'Apparel': ['Jacket', 'Pants', 'Gloves', 'Helmet', 'Boots'],
  'Accessories': ['Luggage', 'Phone Mount', 'Phone Charger', 'Crash Bars', 'Hand Guards'],
};
const CONDITIONS = ['New', 'Used – Normal Use', 'Used – Fair', 'NOS'];

// Category prefix for SKU generation
const CATEGORY_PREFIX = {
  'Engine & Drivetrain': 'MOTO',
  'Bodywork': 'MOTO',
  'Electrical': 'MOTO',
  'Suspension & Wheels': 'MOTO',
  'Apparel': 'APP',
  'Accessories': 'ACC',
};

// Condition letter mapping for SKU
const CONDITION_LETTER = {
  'New': 'N',
  'Used – Normal Use': 'U',
  'Used – Fair': 'F',
  'NOS': 'O',
};

// Generate a short abbreviation from a string (first 3-4 chars)
function abbreviate(str, len = 4) {
  if (!str) return '';
  return str.trim().substring(0, len).toUpperCase().replace(/[^A-Z0-9]/g, '');
}

// Generate SKU based on category, condition, make, part type, and sequence
function generateSKU(make, partCategory, partType, condition, products) {
  const prefix = CATEGORY_PREFIX[partCategory] || 'GEN';
  const condLetter = CONDITION_LETTER[condition] || 'N';
  const makeAbbr = make ? abbreviate(make.name, 3) : 'XXX';
  const typeAbbr = partType ? abbreviate(partType, 4) : 'XXXX';

  // Find max sequence number from existing products in same category
  let maxSeq = 0;
  const categoryPrefixRegex = new RegExp(`^${prefix}-[NUFO]-[A-Z0-9]+-[A-Z0-9]+-(\\d+)$`);
  if (Array.isArray(products)) {
    products.forEach(p => {
      if (p?.sku) {
        const match = p.sku.match(categoryPrefixRegex);
        if (match) {
          const seq = parseInt(match[1], 10);
          if (seq > maxSeq) maxSeq = seq;
        }
      }
    });
  }

  const seq = String(maxSeq + 1).padStart(4, '0');
  return `${prefix}-${condLetter}-${makeAbbr}-${typeAbbr}-${seq}`;
}

// Generate SEO-friendly title from form data
function generateTitle(make, partCategory, partType, model, yearFrom, condition) {
  const parts = [];

  // Brand (make name)
  if (make?.name) {
    parts.push(make.name);
  }

  // Product type
  if (partType) {
    parts.push(partType);
  }

  // Model
  if (model?.trim()) {
    parts.push(model.trim());
  }

  // Year
  if (yearFrom) {
    parts.push(yearFrom.toString());
  }

  // Condition
  if (condition) {
    parts.push(condition);
  }

  return parts.join(' ') || '';
}

export default function ProductForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [allProducts, setAllProducts] = useState([]);

  const [form, setForm] = useState({
    title: '',
    sku: '',
    partType: '',
    partCategory: '',
    condition: '',
    price: '',
    compareAtPrice: '',
     stock: '',
     isAvailable: true,
     isHotDeal: false,
     makeId: '',
    model: '',
    yearFrom: '',
    yearTo: '',
    images: [],
    description: '',
    fitment: [],
  });

  const [deleteDialog, setDeleteDialog] = useState(null);

  // Get current make object from makeId
  const currentMake = makes.find(m => m._id === form.makeId);

  // Load models when make changes
  useEffect(() => {
    if (form.makeId) {
      const make = makes.find(m => m._id === form.makeId);
      if (make) {
        getModelsByMake(make.name)
          .then(data => setModels(data.sort((a, b) => a.name.localeCompare(b.name))))
          .catch(() => setModels([]));
      }
    } else {
      setModels([]);
    }
  }, [form.makeId, makes]);

  // Available part types for current category
  const currentTypes = PART_TYPES[form.partCategory] || [];

  // Auto-generate title when dependent fields change (only in add mode)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isEdit && form.title && (form.makeId || form.partCategory || form.model || form.yearFrom || form.condition)) {
      const newTitle = generateTitle(currentMake, form.partCategory, form.partType, form.model, form.yearFrom, form.condition);
      if (newTitle && newTitle !== form.title) {
        setForm(prev => ({ ...prev, title: newTitle }));
      }
    }
  }, [form.makeId, form.partCategory, form.partType, form.model, form.yearFrom, form.condition, isEdit, currentMake]);

  // Auto-generate SKU when dependent fields change (only in add mode)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isEdit && form.sku && (form.makeId || form.partCategory || form.partType || form.condition)) {
      const newSku = generateSKU(currentMake, form.partCategory, form.partType, form.condition, allProducts);
      if (newSku && newSku !== form.sku) {
        setForm(prev => ({ ...prev, sku: newSku }));
      }
    }
  }, [form.makeId, form.partCategory, form.partType, form.condition, isEdit, currentMake, allProducts]);

  // Initial load: fetch makes and generate SKU/title for new products
  useEffect(() => {
    setLoading(true);
    Promise.all([getMakes()])
      .then(async ([makesData]) => {
        setMakes(makesData);
        let productsData = [];
        try {
          const { getProducts } = await import('../../api');
          const res = await getProducts({ page: 1, limit: 1000 });
          productsData = res?.products || [];
        } catch {}
        setAllProducts(productsData);

        if (isEdit) {
          return getProduct(id).then(data => {
            setForm({
              title: data.title || '',
              sku: data.sku || '',
              partType: data.partType || '',
              partCategory: data.partCategory || '',
              condition: data.condition || '',
              price: data.price?.toString() || '',
              compareAtPrice: data.compareAtPrice?.toString() || '',
             stock: data.stock?.toString() || '',
             isAvailable: data.isAvailable ?? true,
             isHotDeal: data.isHotDeal ?? false,
             makeId: data.makeId || '',
              model: data.model || '',
              yearFrom: data.yearFrom?.toString() || '',
              yearTo: data.yearTo?.toString() || '',
              images: data.images || [],
              description: data.description || '',
              fitment: data.fitment || [],
            });
          });
        } else {
          // Auto-generate initial SKU and title for new product
          const initialSku = generateSKU(null, null, null, 'New', productsData);
          setForm(prev => ({ ...prev, sku: initialSku }));
        }
      })
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  // Regenerate SKU
  const handleRegenerateSKU = useCallback(() => {
    const newSku = generateSKU(currentMake, form.partCategory, form.partType, form.condition, allProducts);
    setForm(prev => ({ ...prev, sku: newSku }));
  }, [currentMake, form.partCategory, form.partType, form.condition, allProducts]);

  // Regenerate title
  const handleRegenerateTitle = useCallback(() => {
    const newTitle = generateTitle(currentMake, form.partCategory, form.partType, form.model, form.yearFrom, form.condition);
    setForm(prev => ({ ...prev, title: newTitle }));
  }, [currentMake, form.partCategory, form.partType, form.model, form.yearFrom, form.condition]);

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    // Validate required fields
    if (!form.title.trim()) {
      setError('Please enter a product title');
      return;
    }
    if (!form.sku.trim()) {
      setError('Please generate or enter a SKU');
      return;
    }
    if (!form.partCategory) {
      setError('Please select a part category');
      return;
    }
    if (!form.condition) {
      setError('Please select a condition');
      return;
    }
    if (!form.price || isNaN(parseFloat(form.price)) || parseFloat(form.price) < 0) {
      setError('Please enter a valid price');
      return;
    }

    setError('');
    setSaving(true);
    try {
      // Auto-generate slug from title + sku
      const slug = `${form.title}-${form.sku}`.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

      // Use partCategory as partType if partType is empty (fallback)
      const partTypeToSend = form.partType || form.partCategory;

      const data = {
        title: form.title.trim(),
        sku: form.sku.trim(),
        slug: slug,
        partType: partTypeToSend,
        partCategory: form.partCategory,
        condition: form.condition,
        price: parseFloat(form.price),
        compareAtPrice: form.compareAtPrice ? parseFloat(form.compareAtPrice) : null,
        stock: form.stock ? parseInt(form.stock, 10) : 0,
        isAvailable: form.isAvailable,
        isHotDeal: form.isHotDeal,
        description: form.description || '',
        fitment: [],
      };

      // Add fitment if make and model selected
      if (form.makeId && form.model && form.yearFrom && form.yearTo) {
        const make = makes.find(m => m._id === form.makeId);
        data.fitment = [{
          make: make?.name || '',
          model: form.model.trim(),
          yearFrom: parseInt(form.yearFrom, 10),
          yearTo: parseInt(form.yearTo, 10),
        }];
      }

      // Log data for debugging
      console.log('Saving product data:', data);

      if (isEdit) {
        await updateProduct(id, data);
      } else {
        await createProduct(data);
      }
      navigate('/admin/products');
    } catch (err) {
      console.error('Product save error:', err);
      setError(err.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    try {
      await (await import('../../api')).deleteProduct(id);
      navigate('/admin/products');
    } catch {
      alert('Delete failed');
    } finally {
      setDeleteDialog(null);
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen"><p>Loading...</p></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/products')}>
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-slate-900">{isEdit ? 'Edit Product' : 'Add Product'}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/admin/products')}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
          {isEdit && (
            <Button variant="destructive" size="sm" onClick={() => setDeleteDialog(true)}>
              Delete
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Basic Info</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Title *</Label>
                  {!isEdit && (
                    <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-slate-600" onClick={handleRegenerateTitle} title="Regenerate title">
                      <RefreshCw className="w-3 h-3" />
                    </Button>
                  )}
                </div>
                <Input 
                  value={form.title} 
                  onChange={e => updateField('title', e.target.value)} 
                  readOnly={isEdit}
                  placeholder="Auto-generated from make, model, and part type"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label>SKU *</Label>
                    {!isEdit && (
                      <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-slate-600" onClick={handleRegenerateSKU} title="Generate new SKU">
                        <RefreshCw className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                  <Input 
                    value={form.sku} 
                    onChange={e => updateField('sku', e.target.value)} 
                    readOnly={isEdit}
                    placeholder="Auto-generated"
                  />
                </div>
                <div>
                  <Label>Part Type *</Label>
                  <Select value={form.partCategory} onValueChange={v => updateField('partCategory', v)}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {PART_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Part Subtype</Label>
                  <Select value={form.partType} onValueChange={v => updateField('partType', v)}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {currentTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Condition *</Label>
                  <Select value={form.condition} onValueChange={v => updateField('condition', v)}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Fitment (Make / Model / Year)</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Make</Label>
                <Select value={form.makeId} onValueChange={v => updateField('makeId', v)}>
                  <SelectTrigger><SelectValue placeholder="Select make" /></SelectTrigger>
                  <SelectContent>
                    {makes.map(m => <SelectItem key={m._id} value={m._id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Model</Label>
                <Select value={form.model} onValueChange={v => updateField('model', v)} disabled={models.length === 0}>
                  <SelectTrigger>
                    <SelectValue placeholder={models.length === 0 ? (form.makeId ? "No models available" : "Select a make first") : "Select model"} />
                  </SelectTrigger>
                  <SelectContent>
                    {models.map(m => (
                      <SelectItem key={m._id} value={m.name}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Year From</Label>
                  <Input type="number" value={form.yearFrom} onChange={e => updateField('yearFrom', e.target.value)} placeholder="e.g., 2020" />
                </div>
                <div>
                  <Label>Year To</Label>
                  <Input type="number" value={form.yearTo} onChange={e => updateField('yearTo', e.target.value)} placeholder="e.g., 2024" />
                </div>
              </div>
              <Separator />
              <div className="text-sm text-slate-500">Fitment will be saved when you save the product.</div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Pricing & Stock</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Price (CAD) *</Label>
                  <Input type="number" step="0.01" value={form.price} onChange={e => updateField('price', e.target.value)} />
                </div>
                <div>
                  <Label>Compare at Price</Label>
                  <Input type="number" step="0.01" value={form.compareAtPrice} onChange={e => updateField('compareAtPrice', e.target.value)} />
                </div>
              </div>
              <div>
                <Label>Stock</Label>
                <Input type="number" value={form.stock} onChange={e => updateField('stock', e.target.value)} />
              </div>
               <div className="flex items-center gap-2">
                 <Checkbox id="avail" checked={form.isAvailable} onCheckedChange={v => updateField('isAvailable', v)} />
                 <Label htmlFor="avail">Available for sale</Label>
               </div>
               <div className="flex items-center gap-2">
                 <Checkbox id="hotdeal" checked={form.isHotDeal} onCheckedChange={v => updateField('isHotDeal', v)} />
                 <Label htmlFor="hotdeal" className="text-brand-red font-medium">Include in Hot Deals carousel</Label>
               </div>
             </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Description</CardTitle></CardHeader>
            <CardContent>
              <Textarea
                rows={6}
                value={form.description}
                onChange={e => updateField('description', e.target.value)}
                placeholder="Product description..."
              />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Delete Product
            </DialogTitle>
            <DialogDescription>Are you sure you want to delete this product?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}